import React, { useState } from 'react';

import { Link } from 'react-router-dom';
import { Box, Collapse, IconButton, MenuItem, MenuList, Tooltip } from '@material-ui/core';
import { BiCrown } from 'react-icons/bi';
import { FaAward } from 'react-icons/fa';
import { FiChevronDown } from 'react-icons/fi';
import { TbTrash } from 'react-icons/tb';
import clsx from 'clsx';

import { GridActions } from '@vyce/core/src/components';
import { StaffListItem, USER_BADGES } from '@vyce/core/src/types';
import { useRolesEditorSummary } from '@vyce/core/src/contexts/rolesSaveBarProvider';
import { getAvatar } from '@vyce/core/src/utils/getAvatar';
import { CompanyRolesEditor } from '@vyce/core/src/modules/rolesModule/components/CompanyRolesEditor';
import { useRolesStyles } from '@vyce/core/src/modules/rolesModule/styles';

import { useStaffAccordionStyles } from '../accordionStyles';

interface Props {
  staff: StaffListItem;
  companyId: string;
  /**
   * Whether this person may read the permission catalogue. Without it the
   * catalogue GET 403s, so the row simply doesn't offer to expand rather than
   * opening into a failed load.
   */
  canExpand?: boolean;
  canRemove?: boolean;
  /** When set, the name links through to this route (Legend's admin profile). */
  profileLink?: string;
  onRemoveClick: (staff: StaffListItem) => void;
}

/**
 * One admin/manager of the company. Same lazy-expand mechanics as the per-company
 * accordion in rolesModule — `hasExpanded` is sticky so the editor mounts on first
 * open and keeps its draft through later collapses.
 */
export const StaffAccordionRow: React.FC<Props> = ({
  staff,
  companyId,
  canExpand = true,
  canRemove,
  profileLink,
  onRemoveClick,
}) => {
  const classes = useRolesStyles();
  const rowClasses = useStaffAccordionStyles();
  const [expanded, setExpanded] = useState(false);
  const [hasExpanded, setHasExpanded] = useState(false);
  const [summary, setSummary] = useState<{ assigned: number; total: number } | null>(null);

  const toggle = () => {
    if (!canExpand) return;
    setExpanded(prev => !prev);
    setHasExpanded(true);
  };

  const name = `${staff.first_name} ${staff.last_name}`.trim();
  // A dirty row that's been collapsed or scrolled past would otherwise only exist
  // as a number in the page's Save bar.
  const editorSummary = useRolesEditorSummary(staff.user_id);
  // An owner holds everything regardless of module assignments, so a count there
  // would understate it.
  const summaryText = staff.owner
    ? 'Full access to all modules'
    : summary
    ? `${summary.assigned} of ${summary.total} modules assigned`
    : '';

  return (
    <div className={classes.companyRow}>
      <div
        className={clsx(classes.companyHeader, !canExpand && classes.companyHeaderStatic)}
        onClick={toggle}>
        <img
          className={rowClasses.avatar}
          src={staff.photo?.url || staff.avatar || getAvatar(staff.gender)}
          alt={name}
        />

        <div className={classes.companyInfo}>
          <div className={classes.companyName}>
            {/* Stop the click bubbling: the header toggles the row, the name navigates. */}
            {profileLink ? (
              <Link
                to={profileLink}
                className={rowClasses.profileLink}
                onClick={event => event.stopPropagation()}>
                {name}
              </Link>
            ) : (
              name
            )}
            {staff.owner && (
              <Tooltip title="Owner">
                <span className={classes.ownerCrown}>
                  <BiCrown size={16} />
                </span>
              </Tooltip>
            )}
            {staff.badges?.includes(USER_BADGES.MOBILE_APP_ADMIN_TUTORIAL_COMPLETED) && (
              <Tooltip title="Vyce Certified">
                <span className={rowClasses.badgeIcon}>
                  <FaAward size={16} />
                </span>
              </Tooltip>
            )}
            {editorSummary?.isDirty && <span className={classes.unsavedChip}>Unsaved</span>}
          </div>

          <div className={classes.companySubtitle}>
            {staff.email}
            {!!summaryText && (
              <>
                <span className={rowClasses.separator}>·</span>
                {summaryText}
              </>
            )}
          </div>
        </div>

        {/* Stop the click bubbling to the header, or opening the menu toggles the row. */}
        {canRemove && !staff.owner && (
          <Box onClick={event => event.stopPropagation()} display="flex">
            <GridActions
              renderEnrichedList={({ closeAnchorEl }) => (
                <MenuList>
                  <MenuItem
                    onClick={() => {
                      closeAnchorEl();
                      onRemoveClick(staff);
                    }}>
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <TbTrash size={14} /> Remove Admin
                    </Box>
                  </MenuItem>
                </MenuList>
              )}
            />
          </Box>
        )}

        {canExpand && (
          <IconButton size="small" className={classes.detailToggle}>
            <span className={clsx(classes.chevron, expanded && classes.chevronOpen)}>
              <FiChevronDown size={18} />
            </span>
          </IconButton>
        )}
      </div>

      {/* Not mounted at all without the right — the editor's hooks would fire
          requests that are guaranteed to 403. */}
      {canExpand && (
        <Collapse in={expanded}>
          <CompanyRolesEditor
            companyId={companyId}
            userId={staff.user_id}
            staffItem={staff}
            enabled={hasExpanded}
            editorId={staff.user_id}
            label={name}
            onSummaryChange={(assigned, total) => setSummary({ assigned, total })}
          />
        </Collapse>
      )}
    </div>
  );
};
